import React, { useState } from "react";
import {
  Form,
  Space,
  Modal,
  Upload,
  Button,
  Table,
  message,
  Typography,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../../service/apiRequest";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Updated sampleTemplate with all columns you want to import
const sampleTemplate = [
  {
    product_name: "",
    product_code: "",
    brand_name: "",
    category_type: "",
    category_main: "",
    sub_category: "",
    size: "",
    gst: "",
    hsn: "",
    unit_type: "",
    unit_value: "",
    product_qty: "",
    product_price: "",
    product_mrp: "",
    product_discounted_price: "",
    product_description: "",
  },
];


const ImportProductModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Download sample Excel file
  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ProductTemplate");
    XLSX.writeFile(wb, "product_import_template.xlsx");
  };

  // Upload and parse Excel
  const handleUpload = async (file: File) => {
    setLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false, });

      if (!json.length) {
        message.warning("The Excel file is empty.");
        setParsedData([]);
        return false;
      }

      setParsedData(json);
      message.success("File loaded successfully!");
    } catch (err) {
      console.error(err);
      message.error("Invalid Excel format.");
      setParsedData([]);
    } finally {
      setLoading(false);
    }

    return false;
  };

  // Send parsed data to backend
  const handleImport = async () => {
    try {
      await apiRequest({
        method: "post",
        url: "/import-medical-product",
        data: parsedData,
      });

      message.success("Products imported successfully!");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Import failed. Check data format."
      );
    }
  };

  return (
    <Modal
      title="Import Products"
      width="85%"
      open={visible}
      onCancel={onClose}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Step 1">
          <Space direction="vertical">
            <Text type="secondary">Download and fill in the sample template</Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        <Form.Item label="Step 2">
          <Space direction="vertical">
            <Text type="secondary">Upload filled template</Text>
            <Upload
              accept=".xlsx"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: "block" }}>
            Preview of Imported Data:
          </Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, i) => String(i)}
            size="small"
            scroll={{ y: 300 }}
            pagination={false}
            columns={Object.keys(parsedData[0]).map((key) => ({
              title: key,
              dataIndex: key,
              key,
              width: 150,
              ellipsis: true,
            }))}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportProductModal;
